import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_CATS = ["amministrazione", "cucina", "bar", "magazzino", "marketing", "pulizia", "manutenzione", "personale", "fornitori"];
const DEFAULT_STATUSES = ["da fare", "in corso", "completato", "bloccato"];
const DEFAULT_PRIORITIES = ["alta", "media", "bassa"];

export default function Tasks() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — task mutations gate on tasks.write.
  const canWrite = can("tasks.write");
  const CATS = lists.task_categories?.length ? lists.task_categories : DEFAULT_CATS;
  const STATUSES = lists.task_statuses?.length ? lists.task_statuses : DEFAULT_STATUSES;
  const PRIORITIES = lists.task_priorities?.length ? lists.task_priorities : DEFAULT_PRIORITIES;
  const [list, setList] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "amministrazione", assignee: "", priority: "media", date: new Date().toISOString().slice(0, 10), status: "da fare", recurring: null });

  const load = async () => {
    const [t, e] = await Promise.all([api.get("/tasks"), api.get("/employees")]);
    setList(t.data); setEmps(e.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/tasks", form); toast.success("Task creata"); setOpen(false); load(); } catch { toast.error("Errore"); }
  };
  const setStatus = async (id, status) => { await api.put(`/tasks/${id}`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/tasks/${id}`); load(); };

  const grouped = {};
  for (const t of list) {
    const key = t.status || "da fare";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  }

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow="Agenda" title="Task & attività"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
            <DialogTrigger asChild><Button data-testid="new-task-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuova task</Button></DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Nuova task</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Titolo</Label><Input data-testid="task-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Categoria</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Priorità</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Assegnatario</Label><Select value={form.assignee} onValueChange={(v) => setForm({ ...form, assignee: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={`${e.first_name} ${e.last_name}`}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="task-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {STATUSES.map((s) => (
          <div key={s} className="border border-[#E2D9C8] rounded-lg p-4 bg-[#F8F4EB]">
            <div className="label-eyebrow mb-3">{s} <span className="text-neutral-400">· {grouped[s]?.length || 0}</span></div>
            <div className="space-y-2">
              {(grouped[s] || []).map((t) => (
                <div key={t.id} className="bg-white border border-[#E2D9C8] rounded p-3" data-testid={`task-${t.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="font-medium text-sm">{t.title}</div>
                    {canWrite && (
                      <button onClick={() => del(t.id)} className="text-neutral-300 hover:text-red-700" data-testid={`del-task-${t.id}`}><Trash2 size={12} strokeWidth={1.5} /></button>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{t.assignee} · {t.date}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.priority === "alta" ? "bg-red-100 text-red-800" : t.priority === "media" ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-600"}`}>{t.priority}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3EDE1] capitalize">{t.category}</span>
                  </div>
                  {canWrite ? (
                  <Select value={t.status} onValueChange={(v) => setStatus(t.id, v)}>
                    <SelectTrigger className="mt-2 h-8 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                  ) : (
                    <div className="mt-2 text-xs px-2 py-1 border border-[#E2D9C8] rounded inline-block" data-testid={`task-status-ro-${t.id}`}>{t.status}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
